import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventCard from "@/components/feature/EventCard";
import { events } from "@/mocks/events";
import { categories } from "@/mocks/categories";
import FilterBar from "./components/FilterBar";

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const query = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "updated";

  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = events.filter((event) => {
      const matchCategory = category === "all" || event.categorySlug === category;
      const matchQuery =
        !q ||
        event.title.toLowerCase().includes(q) ||
        event.summary.toLowerCase().includes(q) ||
        event.category.toLowerCase().includes(q) ||
        event.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCategory && matchQuery;
    });

    return [...list].sort((a, b) => {
      if (sort === "heat") return b.heat - a.heat;
      if (sort === "evidence") return b.evidenceCount - a.evidenceCount;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [category, query, sort]);

  const updateParams = (next: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  const activeCategoryName =
    category === "all" ? "全部类型" : categories.find((item) => item.slug === category)?.name || "全部类型";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "事件档案 · 131吃瓜证据合集",
      description: "按事件类型分类整理的吃瓜证据合集，支持关键词搜索与排序。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "事件档案", item: "/events" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "事件档案列表",
      numberOfItems: filtered.length,
      itemListElement: filtered.map((event, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: event.title,
        url: `/event/${event.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="事件档案 · 131吃瓜证据合集分类整理"
        description="131吃瓜事件档案按娱乐事件、直播风波、品牌代言等类型分类整理实锤证据，支持关键词搜索与热度排序，每条事件都标注证据数量与最后更新时间。"
        keywords="131吃瓜,事件档案,证据合集,实锤,分类整理"
        path="/events"
        type="website"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="w-full bg-background-100/40 pt-24 md:pt-32 pb-10 md:pb-14 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "事件档案" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
              事件档案总览
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-7 text-foreground-500">
              每一份证据合集都从公开可见素材出发，按类型归档并标注来源。先选类型，再看时间线，最后对照前后说法。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-8 md:py-12">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <FilterBar
              categories={categories}
              activeCategory={category}
              keyword={keyword}
              sort={sort}
              resultCount={filtered.length}
              onKeywordInput={setKeyword}
              onKeywordSubmit={(value) => updateParams({ q: value.trim() })}
              onCategoryChange={(slug) => updateParams({ category: slug === "all" ? "" : slug })}
              onSortChange={(value) => updateParams({ sort: value })}
            />

            <div className="mt-6 flex flex-wrap items-center gap-2 text-xs md:text-sm text-foreground-500">
              <span>当前筛选：</span>
              <span className="px-2.5 py-1 rounded-md bg-background-100 border border-background-200 text-foreground-700">
                {activeCategoryName}
              </span>
              {query && (
                <span className="px-2.5 py-1 rounded-md bg-background-100 border border-background-200 text-foreground-700">
                  关键词「{query}」
                </span>
              )}
              {(query || category !== "all") && (
                <button
                  type="button"
                  onClick={() => updateParams({ q: "", category: "" })}
                  className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-400 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line" />
                  清除筛选
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filtered.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="mt-10 rounded-lg border border-background-200 bg-background-100 py-16 px-6 text-center">
                <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-md bg-background-200/70 text-foreground-500 text-2xl">
                  <i className="ri-search-eye-line" />
                </span>
                <p className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有找到匹配的事件</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词，或回到全部类型看看最新收录的档案。</p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateParams({ q: "", category: "" })}
                    className="px-5 py-2.5 rounded-md border border-background-200 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清除全部筛选
                  </button>
                  <Link
                    to="/about#submit"
                    className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    提交线索
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}